/**
 * /submit-parcel — 提交国际包裹
 *
 * 设计稿: _design_reference/mini-submit-parcel.html
 * 参数来源: ?expressOrderNo= (从仓库页创建包裹后跳转)
 *
 * 流程:
 *   1. useExpressOrderDetail       → 商品列表 + 绑定地址
 *   2. useServiceItems             → 打包服务列表 (external_packaging / packaging_details)
 *   3. useExpressRoutes            → 可用线路 (依赖地址 + 已选服务)
 *   4. useCalculateExpressFee      → 实时费用明细 (路线/服务/保险/优惠券变化时重算)
 *   5. useAvailableCoupons         → 可用优惠券列表
 *   6. useSubmitExpressOrder       → 提交包裹 → expressOrderNo → 跳 /shipment
 *
 * 规则:
 * - 所有金额来自 API，不写死任何费用
 * - 所有文案走 useTranslation("submit_parcel") + t()
 * - 无 expressOrderNo 时显示空态 + 引导去仓库
 * - fee.isCarriable === false 时提交按钮 disabled
 */
import {
  type AddressVo,
  type AvailableCoupon,
  type LogisticsRoute,
  type ServiceItem,
  useAddresses,
  useAvailableCoupons,
  useCalculateExpressFee,
  useExpressOrderDetail,
  useExpressRoutes,
  useServiceItems,
  useSubmitExpressOrder,
} from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgEmpty } from "../components/TgEmpty";
import { TgHeader } from "../components/TgHeader";
import { TgMain } from "../components/TgMain";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuthToken } from "../lib/auth";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getParam(search: string, key: string): string {
  const u = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return u.get(key) ?? "";
}

/** Split services: external_packaging (single-select) vs packaging_details (multi-select). */
function splitServices(items: ServiceItem[]): {
  methods: ServiceItem[];
  details: ServiceItem[];
} {
  const methods: ServiceItem[] = [];
  const details: ServiceItem[] = [];
  for (const s of items) {
    if (s.type === "external_packaging") methods.push(s);
    else if (s.type === "packaging_details") details.push(s);
    else methods.push(s); // unknown type → treat as outer method so it's still selectable
  }
  return { methods, details };
}

/** Sort carriable-first, then cheapest. Returns sorted list + cheapest-carriable id. */
function sortRoutes(routes: LogisticsRoute[]): {
  sorted: LogisticsRoute[];
  cheapestId: string | null;
} {
  const sorted = [...routes].sort((a, b) => {
    if (a.carriable !== b.carriable) return a.carriable ? -1 : 1;
    const ca = a.totalCostNumber ?? Number.POSITIVE_INFINITY;
    const cb = b.totalCostNumber ?? Number.POSITIVE_INFINITY;
    return ca - cb;
  });
  const cheapest = sorted.find((r) => r.carriable && r.totalCostNumber != null);
  return { sorted, cheapestId: cheapest?.routeId ?? null };
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function SubmitParcelPage() {
  const { t } = useTranslation("submit_parcel");
  const authToken = useAuthToken();
  const navigate = useNavigate();

  // Read ?expressOrderNo= from URL
  const searchStr = useRouterState({ select: (s) => s.location.searchStr ?? "" });
  const expressOrderNo = getParam(searchStr, "expressOrderNo") || null;
  const addressIdParam = getParam(searchStr, "addressId") || null;

  // ── Data queries ──────────────────────────────────────────────────────────

  const detailQ = useExpressOrderDetail(authToken, expressOrderNo);
  const addressesQ = useAddresses(authToken);
  const servicesQ = useServiceItems("RUB");
  const couponQ = useAvailableCoupons(authToken, expressOrderNo);

  // Resolve delivery address: prefer URL param, then default
  const selectedAddress = useMemo<AddressVo | null>(() => {
    const list = addressesQ.data ?? [];
    if (addressIdParam) return list.find((a) => a.id === addressIdParam) ?? null;
    return list.find((a) => a.isDefault) ?? list[0] ?? null;
  }, [addressesQ.data, addressIdParam]);

  const effectiveAddressId = addressIdParam ?? selectedAddress?.id ?? null;

  // ── Service selection ─────────────────────────────────────────────────────

  const { methods, details } = useMemo(
    () => splitServices(servicesQ.data ?? []),
    [servicesQ.data],
  );

  const [methodId, setMethodId] = useState<string | null>(null);
  const [detailIds, setDetailIds] = useState<Set<string>>(new Set());

  // Auto-select first method once loaded
  useEffect(() => {
    if (methodId == null && methods.length > 0) {
      setMethodId(methods[0]?.id ?? null);
    }
  }, [methods, methodId]);

  const serviceItemIdList = useMemo(() => {
    const ids = methodId ? [methodId] : [];
    return [...ids, ...detailIds];
  }, [methodId, detailIds]);

  // ── Route selection ───────────────────────────────────────────────────────

  const routesQ = useExpressRoutes(
    authToken,
    { expressOrderNo, addressId: effectiveAddressId, serviceItemIdList },
    "RUB",
  );
  const { sorted: sortedRoutes, cheapestId } = useMemo(
    () => sortRoutes(routesQ.data ?? []),
    [routesQ.data],
  );
  const [routeId, setRouteId] = useState<string | null>(null);

  // Auto-select first carriable route when list reloads
  useEffect(() => {
    if (sortedRoutes.length === 0) return;
    const stillValid = routeId && sortedRoutes.some((r) => r.routeId === routeId && r.carriable);
    if (!stillValid) {
      const first = sortedRoutes.find((r) => r.carriable);
      setRouteId(first?.routeId ?? null);
    }
  }, [sortedRoutes, routeId]);

  // ── Coupon ────────────────────────────────────────────────────────────────

  const coupons: AvailableCoupon[] = couponQ.data ?? [];
  const [couponId, setCouponId] = useState<string | null>(null);

  // ── Notes ─────────────────────────────────────────────────────────────────

  const [remarks, setRemarks] = useState("");

  // ── Live fee calculation ───────────────────────────────────────────────────

  const calcMut = useCalculateExpressFee(authToken, "RUB");
  const calcKey = `${routeId}|${serviceItemIdList.join(",")}|${couponId ?? ""}`;
  const lastCalcKey = useRef<string>("");
  const calcMutate = calcMut.mutate;

  useEffect(() => {
    if (!expressOrderNo || !effectiveAddressId || !routeId || serviceItemIdList.length === 0) return;
    if (lastCalcKey.current === calcKey) return;
    lastCalcKey.current = calcKey;
    calcMutate({
      expressOrderNo,
      addressId: effectiveAddressId,
      routeId,
      serviceItemIdList,
      ...(couponId ? { couponCustomerId: couponId } : {}),
    });
  }, [calcKey, calcMutate, expressOrderNo, effectiveAddressId, routeId, serviceItemIdList, couponId]);

  const fee = calcMut.data ?? null;

  // ── Submit ────────────────────────────────────────────────────────────────

  const submitMut = useSubmitExpressOrder(authToken, "RUB");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const canSubmit =
    !!expressOrderNo &&
    !!effectiveAddressId &&
    !!routeId &&
    serviceItemIdList.length > 0 &&
    (fee?.isCarriable ?? false) &&
    !submitMut.isPending;

  const doSubmit = () => {
    setSubmitError(null);
    if (!expressOrderNo || !effectiveAddressId || !routeId) return;
    submitMut.mutate(
      {
        expressOrderNo,
        addressId: effectiveAddressId,
        routeId,
        serviceItemIdList,
        ...(couponId ? { couponCustomerId: couponId } : {}),
        ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
      },
      {
        onSuccess: (payOrderNo) => {
          navigate({
            to: "/shipment" as never,
            search: { orderNo: payOrderNo } as never,
          });
        },
        onError: (e) => setSubmitError(e instanceof Error ? e.message : t("error.submit")),
      },
    );
  };

  // ── No order in URL → empty state ─────────────────────────────────────────

  if (!expressOrderNo) {
    return (
      <TgPage tabbar={false}>
        <TgHeader title={t("head.title")} back />
        <div className="tg-body" style={{ flex: 1 }}>
          <TgEmpty
            icon="📦"
            title={t("empty.title")}
            hint={t("empty.hint")}
            cta={
              <button
                type="button"
                className="tg-main"
                style={{ position: "static", borderRadius: 10, width: "100%" }}
                onClick={() => navigate({ to: "/warehouse" as never })}
              >
                {t("empty.cta")}
              </button>
            }
          />
        </div>
      </TgPage>
    );
  }

  const selectedRoute = sortedRoutes.find((r) => r.routeId === routeId) ?? null;
  const detail = detailQ.data;
  const itemCount = detail?.items.length ?? 0;
  const weightKg =
    detail && detail.totalWeightG > 0
      ? (detail.totalWeightG / 1000).toFixed(2)
      : null;

  // ── Submit button label ────────────────────────────────────────────────────

  const submitLabel = submitMut.isPending
    ? t("receipt.submitting")
    : fee
      ? t("receipt.submit", {
          total: formatPrice(fee.totalAfterDiscountMinor, "ru-RU", "RUB"),
        })
      : t("receipt.submit_plain");

  return (
    <TgPage tabbar={false}>
      <TgHeader title={t("head.title")} back />

      <div className="tg-body" style={{ flex: 1 }}>

        {/* ── Items card ───────────────────────────────────────────────── */}
        <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
          <div
            style={{
              padding: "8px 12px",
              background: "var(--color-surface)",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {detailQ.isLoading
              ? t("state.loading")
              : t("items.title", {
                  count: itemCount,
                  ...(weightKg ? { weight: weightKg } : {}),
                })}
          </div>

          {detailQ.isLoading ? (
            <div style={{ padding: "12px" }}>
              <TgSkeleton height={40} />
              <div style={{ height: 6 }} />
              <TgSkeleton height={40} />
            </div>
          ) : (detail?.items ?? []).map((item, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: backend may omit orderItemId
              key={item.orderItemId ?? `${item.subject}-${i}`}
              style={{
                display: "grid",
                gridTemplateColumns: "40px 1fr auto",
                gap: 8,
                padding: "8px 12px",
                alignItems: "center",
                borderTop: "1px solid var(--color-border)",
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 8,
                  background: "var(--color-surface)",
                  overflow: "hidden",
                  display: "grid",
                  placeItems: "center",
                  fontSize: 20,
                }}
              >
                {item.mainImg ? (
                  <img
                    src={item.mainImg}
                    alt=""
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  "📦"
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                {/* Source-platform title — NOT translated */}
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.subject}
                </div>
                <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
                  × {item.quantity}
                </div>
              </div>
              {/* Price field: backend detail has no per-item price — omit rather than fake */}
              <div style={{ fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 900 }}>
                —
              </div>
            </div>
          ))}
        </div>

        {/* ── Recipient + route cards ──────────────────────────────────── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {/* Recipient */}
          <div
            style={{
              background: "linear-gradient(180deg,var(--color-primary-soft),#fff)",
              border: "1.5px solid var(--color-primary-fill)",
              borderRadius: 10,
              padding: 10,
              fontSize: 10,
            }}
          >
            <strong style={{ fontSize: 11 }}>📍 {t("recipient.title")}</strong>
            <div style={{ marginTop: 4 }}>
              {addressesQ.isLoading ? (
                <TgSkeleton height={12} width={80} />
              ) : selectedAddress ? (
                <>
                  <div style={{ fontWeight: 700 }}>{selectedAddress.recipientName}</div>
                  <div
                    style={{
                      color: "var(--color-text-muted)",
                      marginTop: 2,
                      lineHeight: 1.3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      WebkitLineClamp: 2,
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {selectedAddress.city || selectedAddress.country}
                  </div>
                </>
              ) : (
                <span style={{ color: "var(--color-text-muted)" }}>{t("recipient.none")}</span>
              )}
            </div>
          </div>

          {/* Selected route */}
          <div
            style={{
              background: "linear-gradient(180deg,#FFF0EB,#fff)",
              border: "1.5px solid var(--color-cta)",
              borderRadius: 10,
              padding: 10,
              fontSize: 10,
            }}
          >
            <strong style={{ fontSize: 11 }}>✈ {t("line.title")}</strong>
            <div style={{ marginTop: 4 }}>
              {routesQ.isLoading || routesQ.isFetching ? (
                <TgSkeleton height={12} width={80} />
              ) : selectedRoute ? (
                <>
                  <div
                    style={{
                      color: "var(--color-cta)",
                      fontWeight: 800,
                      fontFamily: "var(--font-display)",
                    }}
                  >
                    {selectedRoute.totalCostDisplay
                      ? `${selectedRoute.totalCostDisplay} ₽`
                      : selectedRoute.name}
                  </div>
                  {selectedRoute.minDeliveredDays != null && (
                    <div style={{ color: "var(--color-text-muted)", marginTop: 2 }}>
                      {selectedRoute.name} · {selectedRoute.minDeliveredDays}
                      {selectedRoute.maxDeliveredDays != null &&
                        `–${selectedRoute.maxDeliveredDays}`}{" "}
                      {t("days_abbr")}
                    </div>
                  )}
                </>
              ) : (
                <span style={{ color: "var(--color-text-muted)" }}>{t("line.none")}</span>
              )}
            </div>
          </div>
        </div>

        {/* ── Route picker list ─────────────────────────────────────────── */}
        {(routesQ.isLoading || routesQ.isFetching || sortedRoutes.length > 0) && (
          <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
            <div
              style={{
                padding: "8px 12px",
                background: "var(--color-surface)",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {t("line.title")}
            </div>
            {routesQ.isLoading || routesQ.isFetching ? (
              <div style={{ padding: 12 }}>
                <TgSkeleton height={50} />
                <div style={{ height: 6 }} />
                <TgSkeleton height={50} />
              </div>
            ) : (
              sortedRoutes.map((route) => (
                <RouteCard
                  key={route.routeId}
                  route={route}
                  selected={route.routeId === routeId}
                  cheapest={route.routeId === cheapestId}
                  onSelect={() => route.carriable && setRouteId(route.routeId)}
                />
              ))
            )}
          </div>
        )}

        {/* ── Packing services ─────────────────────────────────────────── */}
        {(servicesQ.isLoading || methods.length > 0 || details.length > 0) && (
          <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
            <div
              style={{
                padding: "8px 12px",
                background: "var(--color-surface)",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {t("packing.title")}
            </div>
            {servicesQ.isLoading ? (
              <div style={{ padding: 12 }}>
                <TgSkeleton height={44} />
              </div>
            ) : (
              <>
                {methods.map((s) => (
                  <ServiceCard
                    key={s.id}
                    service={s}
                    selected={methodId === s.id}
                    freeLabel={t("packing.free")}
                    onClick={() => setMethodId(s.id)}
                    radio
                  />
                ))}
                {details.map((s) => (
                  <ServiceCard
                    key={s.id}
                    service={s}
                    selected={detailIds.has(s.id)}
                    freeLabel={t("packing.free")}
                    onClick={() =>
                      setDetailIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(s.id)) next.delete(s.id);
                        else next.add(s.id);
                        return next;
                      })
                    }
                    radio={false}
                  />
                ))}
              </>
            )}
          </div>
        )}

        {/* ── Coupon picker ─────────────────────────────────────────────── */}
        {coupons.length > 0 && (
          <div className="tg-card" style={{ padding: 0, overflow: "hidden" }}>
            <div
              style={{
                padding: "8px 12px",
                background: "var(--color-surface)",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {t("coupon.title")}
            </div>
            <CouponCard
              selected={couponId === null}
              label={t("coupon.none")}
              sub={null}
              onClick={() => setCouponId(null)}
            />
            {coupons.map((c) => (
              <CouponCard
                key={c.couponCustomerId}
                selected={couponId === c.couponCustomerId}
                label={c.name}
                sub={
                  c.discountKopecks != null && c.discountKopecks > 0
                    ? `− ${formatPrice(c.discountKopecks, "ru-RU", "RUB")}`
                    : c.discountPercent != null
                      ? `−${c.discountPercent}%`
                      : null
                }
                onClick={() => setCouponId(c.couponCustomerId)}
              />
            ))}
          </div>
        )}

        {/* ── Customs note (from fee.countryTips) ──────────────────────── */}
        {fee?.countryTips && (
          <div className="tg-card">
            <h4 className="tg-card__h">{t("customs.title")}</h4>
            <div
              style={{
                background: "var(--color-primary-soft)",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 11,
                color: "var(--color-primary-fill)",
                display: "flex",
                gap: 8,
              }}
            >
              <span>✓</span>
              <span>{fee.countryTips}</span>
            </div>
          </div>
        )}

        {/* ── Notes ────────────────────────────────────────────────────── */}
        <div className="tg-card">
          <h4 className="tg-card__h">{t("notes.title")}</h4>
          <textarea
            value={remarks}
            maxLength={200}
            onChange={(e) => setRemarks(e.target.value)}
            style={{
              width: "100%",
              border: "1px solid var(--color-border)",
              borderRadius: 8,
              padding: "8px 10px",
              fontSize: 11,
              background: "#fff",
              fontFamily: "inherit",
              minHeight: 50,
              resize: "vertical",
              boxSizing: "border-box",
            }}
            placeholder={t("notes.placeholder")}
          />
        </div>

        {/* ── Fee receipt ───────────────────────────────────────────────── */}
        <div
          className="tg-card"
          style={{
            background: "linear-gradient(135deg,var(--color-text),#1A2924)",
            color: "#fff",
            border: 0,
          }}
        >
          {fee ? (
            <>
              {fee.logisticsFeeMinor > 0 && (
                <ReceiptRow
                  label={
                    selectedRoute
                      ? `${t("receipt.freight")} · ${selectedRoute.name}`
                      : t("receipt.freight")
                  }
                  value={formatPrice(fee.logisticsFeeMinor, "ru-RU", "RUB")}
                />
              )}
              {fee.operateFeeMinor > 0 && (
                <ReceiptRow
                  label={t("receipt.operate")}
                  value={formatPrice(fee.operateFeeMinor, "ru-RU", "RUB")}
                />
              )}
              {fee.serviceFeeMinor > 0 && (
                <ReceiptRow
                  label={t("receipt.packing")}
                  value={formatPrice(fee.serviceFeeMinor, "ru-RU", "RUB")}
                />
              )}
              {fee.fuelFeeMinor > 0 && (
                <ReceiptRow
                  label={t("receipt.fuel")}
                  value={formatPrice(fee.fuelFeeMinor, "ru-RU", "RUB")}
                />
              )}
              {fee.insuranceFeeMinor > 0 && (
                <ReceiptRow
                  label={t("receipt.insurance")}
                  value={formatPrice(fee.insuranceFeeMinor, "ru-RU", "RUB")}
                />
              )}
              {fee.couponFeeMinor > 0 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    padding: "4px 0",
                    color: "var(--color-primary)",
                  }}
                >
                  <span>{t("receipt.discount")}</span>
                  <span>− {formatPrice(fee.couponFeeMinor, "ru-RU", "RUB")}</span>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  paddingTop: 8,
                  marginTop: 6,
                  borderTop: "2px solid var(--color-sticker-yellow)",
                }}
              >
                <div style={{ fontSize: 10, color: "rgba(255,255,255,.65)" }}>
                  {t("receipt.total_label")}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 22,
                    fontWeight: 900,
                    color: "var(--color-sticker-yellow)",
                  }}
                >
                  {formatPrice(fee.totalAfterDiscountMinor, "ru-RU", "RUB")}
                </div>
              </div>

              {!fee.isCarriable && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 11,
                    color: "var(--color-sticker-yellow)",
                    fontWeight: 700,
                  }}
                >
                  {t("receipt.not_carriable")}
                </div>
              )}
            </>
          ) : (
            <div style={{ color: "rgba(255,255,255,.6)", fontSize: 13, padding: "8px 0" }}>
              {calcMut.isPending ? t("receipt.calculating") : t("receipt.pick_route")}
            </div>
          )}

          {submitError && (
            <div
              role="alert"
              style={{
                marginTop: 8,
                fontSize: 11,
                color: "var(--color-sticker-yellow)",
                fontWeight: 700,
              }}
            >
              {submitError}
            </div>
          )}
        </div>

      </div>

      {/* ── Sticky CTA ─────────────────────────────────────────────────── */}
      <TgMain disabled={!canSubmit} onClick={doSubmit}>
        {submitLabel}
      </TgMain>
    </TgPage>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Radio({ active }: { active: boolean }) {
  return (
    <div
      style={{
        width: 14,
        height: 14,
        borderRadius: "50%",
        border: `2px solid ${active ? "var(--color-primary-fill)" : "var(--color-border-strong)"}`,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {active && (
        <div
          style={{
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "var(--color-primary-fill)",
          }}
        />
      )}
    </div>
  );
}

function Checkbox({ active }: { active: boolean }) {
  return (
    <div
      style={{
        width: 14,
        height: 14,
        borderRadius: 3,
        border: `2px solid ${active ? "var(--color-primary-fill)" : "var(--color-border-strong)"}`,
        background: active ? "var(--color-primary-fill)" : "#fff",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {active && (
        <div style={{ color: "#fff", fontSize: 9, fontWeight: 900, lineHeight: 1 }}>✓</div>
      )}
    </div>
  );
}

function ServiceCard({
  service,
  selected,
  freeLabel,
  onClick,
  radio,
}: {
  service: ServiceItem;
  selected: boolean;
  freeLabel: string;
  onClick: () => void;
  radio: boolean;
}) {
  return (
    <label
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 10,
        padding: "10px 12px",
        alignItems: "center",
        borderTop: "1px solid var(--color-border)",
        cursor: "pointer",
        background: selected ? "var(--color-primary-soft)" : undefined,
      }}
      onClick={onClick}
    >
      <div>
        <strong style={{ fontSize: 11 }}>{service.title}</strong>
        {service.description && (
          <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
            {service.description}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            color: service.isFree ? "var(--color-primary-fill)" : "inherit",
          }}
        >
          {service.isFree
            ? freeLabel
            : `+ ${formatPrice(service.priceMinor, "ru-RU", "RUB")}`}
        </span>
        {radio ? <Radio active={selected} /> : <Checkbox active={selected} />}
      </div>
    </label>
  );
}

function RouteCard({
  route,
  selected,
  cheapest,
  onSelect,
}: {
  route: LogisticsRoute;
  selected: boolean;
  cheapest: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation("submit_parcel");
  return (
    <label
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 10,
        padding: "10px 12px",
        alignItems: "center",
        borderTop: "1px solid var(--color-border)",
        cursor: route.carriable ? "pointer" : "not-allowed",
        background: selected ? "var(--color-primary-soft)" : undefined,
        opacity: route.carriable ? 1 : 0.45,
      }}
      onClick={route.carriable ? onSelect : undefined}
    >
      <div>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          <strong style={{ fontSize: 11 }}>{route.name}</strong>
          {cheapest && (
            <span
              style={{
                background: "var(--color-cta)",
                color: "#fff",
                fontSize: 8,
                fontWeight: 800,
                padding: "1px 4px",
                borderRadius: 4,
              }}
            >
              ★
            </span>
          )}
        </div>
        {route.minDeliveredDays != null && (
          <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
            {route.minDeliveredDays}
            {route.maxDeliveredDays != null && `–${route.maxDeliveredDays}`} {t("days_abbr")}
          </div>
        )}
        {!route.carriable && route.rejectReason && (
          <div style={{ fontSize: 9, color: "var(--color-error, #e53e3e)", marginTop: 2 }}>
            {route.rejectReason}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 13,
            fontWeight: 900,
            color: selected ? "var(--color-cta)" : "inherit",
          }}
        >
          {route.totalCostDisplay ? `${route.totalCostDisplay} ₽` : "—"}
        </span>
        <Radio active={selected} />
      </div>
    </label>
  );
}

function CouponCard({
  selected,
  label,
  sub,
  onClick,
}: {
  selected: boolean;
  label: string;
  sub: string | null;
  onClick: () => void;
}) {
  return (
    <label
      style={{
        display: "grid",
        gridTemplateColumns: "1fr auto auto",
        gap: 8,
        padding: "10px 12px",
        alignItems: "center",
        borderTop: "1px solid var(--color-border)",
        cursor: "pointer",
        background: selected ? "var(--color-primary-soft)" : undefined,
      }}
      onClick={onClick}
    >
      <strong style={{ fontSize: 11 }}>{label}</strong>
      {sub ? (
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-cta)" }}>{sub}</span>
      ) : (
        <span />
      )}
      <Radio active={selected} />
    </label>
  );
}

function ReceiptRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: 11,
        padding: "4px 0",
        color: "rgba(255,255,255,.85)",
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
